import React from 'react';
import IframeErrorBoundary from './IframeErrorBoundary';
import SwipeCarousel from '../SwipeCarousel';
import { useToast } from '../../hooks/use-toast';

// Fires a friendly toast at most once per session per service when the user
// clicks the "Open in {Service}" fallback link. Also POSTs an analytics event
// (best-effort, fire-and-forget) so admins can spot which embeds fail most.
const useEmbedFallbackToast = () => {
  const { toast } = useToast();
  return (service) => {
    const key = `embedFallbackToastShown_${service}`;
    try {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        const messages = {
          youtube: "Hmm, the YouTube player misbehaved — opened it in a new tab for you.",
          spotify: "Hmm, the Spotify player misbehaved — opened it in a new tab for you.",
          soundcloud: "Hmm, the SoundCloud player misbehaved — opened it in a new tab for you.",
        };
        toast({
          title: 'Opened in new tab',
          description: messages[service] || 'The embed was unresponsive — opened in a new tab.',
        });
      }
    } catch {
      /* sessionStorage unavailable (e.g., privacy mode) — silently skip */
    }
    // Best-effort analytics ping
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/embed-fallback`;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service,
          user_agent: navigator.userAgent,
          path: window.location.pathname,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* network-blocked — ignore */
    }
  };
};

const ytIdFromUrl = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};

const buildSoundcloudSrc = (url) => {
  const sec = url.match(/\/s-([A-Za-z0-9]+)/);
  const secret = sec ? `s-${sec[1]}` : null;
  const base = secret ? url.replace(`/${secret}`, '') : url;
  const wp = new URLSearchParams({
    url: base,
    color: '#e94560',
    auto_play: 'false',
    hide_related: 'true',
    show_comments: 'false',
    show_user: 'true',
    show_reposts: 'false',
    show_teaser: 'true',
  });
  if (secret) wp.set('secret_token', secret);
  return `https://w.soundcloud.com/player/?${wp.toString()}`;
};

const spotifyEmbedFromUrl = (url) => {
  const sm = url.match(/open\.spotify\.com\/(track|episode|show|playlist)\/([a-zA-Z0-9]+)/);
  if (!sm) return null;
  return `https://open.spotify.com/embed/${sm[1]}/${sm[2]}?theme=0`;
};

// Facebook video URL → official video plugin embed
const facebookEmbedFromUrl = (url) => {
  if (!url) return null;
  const isFb = /facebook\.com|fb\.watch/.test(url);
  if (!isFb) return null;
  const u = new URLSearchParams({
    href: url,
    show_text: 'false',
    width: '560',
    autoplay: 'false',
  });
  return `https://www.facebook.com/plugins/video.php?${u.toString()}`;
};

const detectPlatformFromUrl = (url) => {
  if (!url) return null;
  if (/youtube\.com|youtu\.be/.test(url)) return 'youtube';
  if (/soundcloud\.com/.test(url)) return 'soundcloud';
  if (/open\.spotify\.com/.test(url)) return 'spotify';
  if (/facebook\.com|fb\.watch/.test(url)) return 'facebook';
  return null;
};

/**
 * Build per-platform ordered item arrays from a book.
 * Honors `book.media_order` (array of urls) so admin-defined display order is respected.
 *
 * Returns: { youtube, soundcloud, spotify, facebook }
 */
export const buildMediaItems = (book) => {
  if (!book) return { youtube: [], soundcloud: [], spotify: [], facebook: [] };

  const youtube = [];
  const soundcloud = [];
  const spotify = [];
  const facebook = [];

  if (book.youtube_url) {
    const id = ytIdFromUrl(book.youtube_url);
    if (id) youtube.push({ id, url: book.youtube_url, label: 'Logan Crawford Interview' });
  }
  if (book.youtube_url_2) {
    const id = ytIdFromUrl(book.youtube_url_2);
    if (id) youtube.push({ id, url: book.youtube_url_2, label: 'Video Interview' });
  }
  if (book.soundcloud_url) {
    soundcloud.push({
      src: buildSoundcloudSrc(book.soundcloud_url),
      url: book.soundcloud_url,
      label: 'Radio Interview',
    });
  }
  if (book.spotify_url) {
    const src = spotifyEmbedFromUrl(book.spotify_url);
    if (src) spotify.push({ src, url: book.spotify_url, label: 'Spotify' });
  }

  if (Array.isArray(book.media_links)) {
    book.media_links.forEach((link) => {
      if (!link?.url) return;
      // Auto-detect platform if not explicitly set so admins can paste any URL
      const platform = link.type || detectPlatformFromUrl(link.url);
      if (platform === 'youtube') {
        const id = ytIdFromUrl(link.url);
        if (id) youtube.push({ id, url: link.url, label: link.label || 'Video' });
      } else if (platform === 'soundcloud') {
        soundcloud.push({
          src: buildSoundcloudSrc(link.url),
          url: link.url,
          label: link.label || 'Audio',
        });
      } else if (platform === 'spotify') {
        const src = spotifyEmbedFromUrl(link.url);
        if (src) spotify.push({ src, url: link.url, label: link.label || 'Spotify' });
      } else if (platform === 'facebook') {
        const src = facebookEmbedFromUrl(link.url);
        if (src) facebook.push({ src, url: link.url, label: link.label || 'Facebook Video' });
      }
    });
  }

  // Apply media_pinned (pinned items first) then media_order, then natural order
  const order = Array.isArray(book.media_order) ? book.media_order : [];
  const pinned = Array.isArray(book.media_pinned) ? book.media_pinned : [];
  const isPinned = (url) => pinned.includes(url);
  const orderIdx = (url) => {
    const i = order.indexOf(url);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const pinnedRank = (url) => {
    const i = pinned.indexOf(url);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const sortByOrder = (arr) => {
    const indexed = arr.map((it, naturalIdx) => ({ it, naturalIdx }));
    indexed.sort((a, b) => {
      // Pinned items always first; among pinned items, preserve pinned order
      const ap = isPinned(a.it.url) ? 0 : 1;
      const bp = isPinned(b.it.url) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      if (ap === 0) return pinnedRank(a.it.url) - pinnedRank(b.it.url);
      // Non-pinned: media_order, then natural
      const ao = orderIdx(a.it.url);
      const bo = orderIdx(b.it.url);
      if (ao !== bo) return ao - bo;
      return a.naturalIdx - b.naturalIdx;
    });
    return indexed.map((x) => x.it);
  };

  return {
    youtube: sortByOrder(youtube),
    soundcloud: sortByOrder(soundcloud),
    spotify: sortByOrder(spotify),
    facebook: sortByOrder(facebook),
  };
};

const PlatformHeader = ({ icon, title, count }) => (
  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
    {icon}
    {title}
    {count > 1 && <span className="text-sm text-gray-400 font-normal">({count})</span>}
  </h3>
);

const MediaCarousels = ({ book }) => {
  const { youtube, soundcloud, spotify, facebook } = buildMediaItems(book);
  const hasMedia = youtube.length || soundcloud.length || spotify.length || facebook.length;
  const notifyFallback = useEmbedFallbackToast();
  const disabled = Array.isArray(book?.disabled_embeds) ? book.disabled_embeds : [];
  const ytDisabled = disabled.includes('youtube');
  const scDisabled = disabled.includes('soundcloud');
  const spDisabled = disabled.includes('spotify');
  const fbDisabled = disabled.includes('facebook');
  if (!hasMedia) return null;

  return (
    <div className="bg-gradient-to-b from-black/55 to-black/65 backdrop-blur-sm py-12" data-testid="media-embeds-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="space-y-10" data-testid="author-media-embeds">
          {youtube.length > 0 && (
            <SwipeCarousel
              items={youtube}
              testId="youtube"
              ariaLabel="YouTube videos"
              header={
                <PlatformHeader
                  icon={
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" />
                      <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#fff" />
                    </svg>
                  }
                  title="YouTube"
                  count={youtube.length}
                />
              }
              renderItem={(item) => (
                <div>
                  <p className="text-sm text-gray-400 mb-2">{item.label}</p>
                  {ytDisabled ? (
                    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-6 text-center" data-testid="youtube-fallback-only">
                      <p className="text-gray-300 text-sm mb-3">Video opens directly on YouTube for the best experience.</p>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => notifyFallback('youtube')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg"
                        data-testid="youtube-open-link"
                      >
                        ▶ Watch on YouTube
                      </a>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-xl overflow-hidden border border-white/10" style={{ aspectRatio: '16/9' }}>
                        <IframeErrorBoundary fallbackLabel="Video unavailable." url={item.url}>
                          <iframe
                            src={`https://www.youtube.com/embed/${item.id}`}
                            title={item.label}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                            style={{ border: 'none' }}
                          />
                        </IframeErrorBoundary>
                      </div>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => notifyFallback('youtube')}
                          className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 mt-2"
                          data-testid="youtube-open-link"
                        >
                          Watch on YouTube
                        </a>
                      )}
                    </>
                  )}
                </div>
              )}
            />
          )}

          {soundcloud.length > 0 && (
            <SwipeCarousel
              items={soundcloud}
              testId="soundcloud"
              ariaLabel="SoundCloud audio"
              header={
                <PlatformHeader
                  icon={
                    <svg className="h-5 w-5 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.057-.05-.1-.1-.1" />
                    </svg>
                  }
                  title="SoundCloud"
                  count={soundcloud.length}
                />
              }
              renderItem={(item) => (
                <div>
                  <p className="text-sm text-gray-400 mb-2">{item.label}</p>
                  {scDisabled ? (
                    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-6 text-center" data-testid="soundcloud-fallback-only">
                      <p className="text-gray-300 text-sm mb-3">Audio opens directly on SoundCloud for the best experience.</p>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => notifyFallback('soundcloud')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg"
                        data-testid="soundcloud-open-link"
                      >
                        ♪ Listen on SoundCloud
                      </a>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-xl overflow-hidden border border-white/10 bg-zinc-900/50">
                        <IframeErrorBoundary fallbackLabel="Audio unavailable." url={item.url}>
                          <iframe
                            width="100%"
                            height="166"
                            scrolling="no"
                            frameBorder="no"
                            allow="autoplay"
                            src={item.src}
                            title={item.label}
                          />
                        </IframeErrorBoundary>
                      </div>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => notifyFallback('soundcloud')}
                          className="inline-flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300 mt-2"
                          data-testid="soundcloud-open-link"
                        >
                          Listen on SoundCloud
                        </a>
                      )}
                    </>
                  )}
                </div>
              )}
            />
          )}

          {spotify.length > 0 && (
            <SwipeCarousel
              items={spotify}
              testId="spotify"
              ariaLabel="Spotify episodes"
              header={
                <PlatformHeader
                  icon={
                    <svg className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02z" />
                    </svg>
                  }
                  title="Spotify"
                  count={spotify.length}
                />
              }
              renderItem={(item) => (
                <div>
                  <p className="text-sm text-gray-400 mb-2">{item.label}</p>
                  {spDisabled ? (
                    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-6 text-center" data-testid="spotify-fallback-only">
                      <p className="text-gray-300 text-sm mb-3">Episode opens directly on Spotify for the best experience.</p>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => notifyFallback('spotify')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg"
                        data-testid="spotify-open-link"
                      >
                        ♪ Open in Spotify
                      </a>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-xl overflow-hidden border border-white/10">
                        <IframeErrorBoundary fallbackLabel="Spotify embed unavailable." url={item.url}>
                          <iframe
                            src={item.src}
                            width="100%"
                            height="152"
                            frameBorder="0"
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            loading="lazy"
                            title={item.label}
                            style={{ borderRadius: '12px' }}
                          />
                        </IframeErrorBoundary>
                      </div>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => notifyFallback('spotify')}
                          className="inline-flex items-center gap-1.5 text-xs text-green-400 hover:text-green-300 mt-2"
                          data-testid="spotify-open-link"
                        >
                          Open in Spotify
                        </a>
                      )}
                    </>
                  )}
                </div>
              )}
            />
          )}

          {facebook.length > 0 && (
            <SwipeCarousel
              items={facebook}
              testId="facebook"
              ariaLabel="Facebook videos"
              header={
                <PlatformHeader
                  icon={
                    <svg className="h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22 12.073c0-5.523-4.477-10-10-10s-10 4.477-10 10c0 4.991 3.657 9.128 8.438 9.879v-6.987h-2.54V12.07h2.54V9.797c0-2.51 1.493-3.89 3.776-3.89 1.094 0 2.238.197 2.238.197v2.46h-1.26c-1.243 0-1.628.771-1.628 1.563v1.875h2.773l-.443 2.89h-2.33V21.95C18.343 21.196 22 17.06 22 12.073Z" />
                    </svg>
                  }
                  title="Facebook"
                  count={facebook.length}
                />
              }
              renderItem={(item) => (
                <div>
                  <p className="text-sm text-gray-400 mb-2">{item.label}</p>
                  {fbDisabled ? (
                    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-6 text-center" data-testid="facebook-fallback-only">
                      <p className="text-gray-300 text-sm mb-3">Video opens directly on Facebook for the best experience.</p>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => notifyFallback('facebook')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg"
                        data-testid="facebook-open-link"
                      >
                        ▶ Watch on Facebook
                      </a>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-xl overflow-hidden border border-white/10" style={{ aspectRatio: '16/9' }}>
                        <IframeErrorBoundary fallbackLabel="Facebook video unavailable." url={item.url}>
                          <iframe
                            src={item.src}
                            title={item.label}
                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                            allowFullScreen
                            className="w-full h-full"
                            style={{ border: 'none' }}
                          />
                        </IframeErrorBoundary>
                      </div>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => notifyFallback('facebook')}
                          className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 mt-2"
                          data-testid="facebook-open-link"
                        >
                          Watch on Facebook
                        </a>
                      )}
                    </>
                  )}
                </div>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaCarousels;
